if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Fires the moment the slide index changes, so the active thumbnail
        // tracks the image during the swipe. Dawn debounced this by 500ms,
        // which meant the thumbnail only caught up half a second after the
        // swipe (and its momentum) had completely stopped.
        //
        // Safe to run undebounced: SliderComponent only emits slideChanged when
        // currentPage actually changes — not on every scroll frame — and
        // setActiveThumbnail scrolls the rail only when the thumbnail is off
        // screen, so adjacent slides do no extra work.
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          /*
            THE PAGE ONLY MOVES FOR A STACKED GALLERY.

            What follows scrolls the window so the chosen media sits at the top
            of the viewport. That is right for Dawn's `stacked` layout, where
            the media are listed one under another and the one you picked may
            genuinely be off screen.

            It is wrong for every layout this theme actually uses. On desktop
            the media are a transform track and on mobile a scroll-snap slider
            — either way they sit SIDE BY SIDE at the same height, and the one
            you picked is already exactly where the last one was. There is
            nothing to bring into view.

            What it did instead: the guard below fires whenever the gallery's
            top edge is above the viewport, which it is the moment you have
            scrolled down to reach the thumbnails. So changing image yanked the
            page upward to re-pin the gallery to the top of the screen, every
            time, on both breakpoints.

            Gated rather than deleted so the stacked layout keeps working if
            anyone ever switches to it.
          */
          const stacked = !this.dataset.desktopLayout?.includes('thumbnail');
          if (!stacked || !this.mql.matches) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          // Smooth scrolling, when it is on, owns the page's scroll position:
          // a native animated scrollTo would be overwritten on its very next
          // frame. window.lenis exists only while Lenis is actually running,
          // so this falls back to the browser whenever it is not.
          if (window.lenis) {
            window.lenis.scrollTo(top);
          } else {
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        /*
          A media can exist in the viewer with no thumbnail to match it — a
          variant image swapped in before the rail has caught up, or a gallery
          rendered with the thumbnails hidden at this breakpoint. Unguarded,
          reading .dataset off null throws, and because this is the last thing
          setActiveMedia does the throw lands AFTER is-active has moved: the
          stage shows the new photo and the rail is left marking the old one,
          which is precisely the state going stale.
        */
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
